import { DonationFormCategory } from "@simplyk/common";

import { useIsOnboarding } from "../../../../common/hooks/useIsOnboarding";

import { trpc } from "@/common/helpers/trpc";
import { DonationFormWithUserOrOrgAccessOutput } from "@/common/types/trpc";
import { LiveEditorStepKey } from "@/features/LiveFormEditor/Steps/LiveEditorStep.enum";
import { LiveFormEditorMode } from "@/features/LiveFormEditor/types/LiveFormEditor.types";

export const useGetAllDonationFormStepsInOrder = ({
  apiDonationForm,
  editOrCreateMode,
  editorMode = "org",
}: {
  apiDonationForm: DonationFormWithUserOrOrgAccessOutput;
  editOrCreateMode: "edit" | "create";
  editorMode?: LiveFormEditorMode;
}) => {
  const { isOnboarding } = useIsOnboarding();
  const formCategory = apiDonationForm.category;
  const businessModelStepShouldBeSkip = !isOnboarding || editOrCreateMode === "edit";
  const organization = apiDonationForm.organization;
  const taxReceiptsStepShouldBeSkip = !organization?.canGenerateReceipt;
  const isDonorOrFundraiserMode = editorMode === "donor" || editorMode === "fundraiserToken";

  const isQbQueryEnabled = !isDonorOrFundraiserMode;
  const { data: qbConnection } = trpc.getQuickBooksConnection.useQuery(undefined, {
    enabled: isQbQueryEnabled,
  });
  const quickBooksMappingStepShouldBeSkip =
    !isQbQueryEnabled || !qbConnection?.isConnected || !qbConnection?.bankAccountId;

  if (formCategory === DonationFormCategory.PeerToPeerCampaign) {
    // For donor/fundraiserToken modes editing P2P individual campaigns, use restricted steps
    // These are limited to visual/content editing steps only
    if (isDonorOrFundraiserMode) {
      const p2pIndividualSteps: { key: LiveEditorStepKey; shouldBeSkip?: boolean }[] = [
        { key: "title" },
        { key: "fundraising-goal" },
        { key: "description" },
        { key: "color" },
        { key: "banner" },
        { key: "logo" },
        { key: "thank-you-email" },
        { key: "review" },
      ];
      return { steps: p2pIndividualSteps };
    }

    // Order matters - Full P2P steps for org users
    const p2pOrgSteps: { key: LiveEditorStepKey; shouldBeSkip?: boolean }[] = [
      { key: "title" },
      { key: "fundraising-goal" },
      { key: "description" },
      { key: "p2p-settings" },
      { key: "color" },
      { key: "banner" },
      { key: "logo" },
      { key: "suggested-donation-amounts" },

      { key: "tax-receipts", shouldBeSkip: taxReceiptsStepShouldBeSkip },
      { key: "questions" },
      {
        key: "business-model",
        shouldBeSkip: businessModelStepShouldBeSkip,
      },
      { key: "thank-you-email" },
      { key: "quickbooks-mapping", shouldBeSkip: quickBooksMappingStepShouldBeSkip },
      { key: "advanced-settings" },
      { key: "review" },
    ];
    // For org users, use full P2P steps
    return { steps: p2pOrgSteps };
  }

  // Order matters
  const donationFormSteps: { key: LiveEditorStepKey; shouldBeSkip?: boolean }[] = [
    { key: "title" },
    { key: "fundraising-goal" },
    { key: "description" },
    { key: "color" },
    { key: "banner" },
    { key: "logo" },
    { key: "suggested-donation-amounts" },
    {
      key: "tax-receipts",
      shouldBeSkip: taxReceiptsStepShouldBeSkip,
    },
    { key: "questions" },
    {
      key: "business-model",
      shouldBeSkip: businessModelStepShouldBeSkip,
    },
    { key: "thank-you-email" },
    { key: "quickbooks-mapping", shouldBeSkip: quickBooksMappingStepShouldBeSkip },
    { key: "advanced-settings" },
    { key: "review" },
  ];

  return { steps: donationFormSteps };
};
